import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import CenteredAuth from 'dashboard/layouts/auth/types/Centered';
import { useAuth } from 'contexts/AuthContext';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing invite token');
      return;
    }
    API.getIamInviteByToken(token)
      .then((res) => setInvite(res))
      .catch((err) => setError(err?.message || 'Could not load invite'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = () => {
    setAccepting(true);
    API.acceptIamInvite({ token, user_id: user?._id })
      .then(() => {
        toast.success('Invitation accepted');
        navigate('/dashboard/brand/overview');
      })
      .catch((err) => toast.error(err?.message || 'Could not accept'))
      .finally(() => setAccepting(false));
  };

  // Sign-in/up now happen in the AuthModal on the landing. We stash
  // the invite return path in sessionStorage so the modal can route
  // the user back here after authentication (handled by AuthModal's
  // handleSuccess), then full-load the landing.
  const goAuth = () => {
    try {
      sessionStorage.setItem('tpo_post_auth_redirect', `/dashboard/auth/accept-invite?token=${token}`);
    } catch (_) { /* private mode */ }
    navigate('/');
  };
  const goSignIn = goAuth;
  const goSignUp = goAuth;

  const userEmail = (user?.email || user?.email_address || '').toLowerCase();
  const emailMatches = userEmail && invite?.email && userEmail === invite.email;

  return (
    <CenteredAuth
      cardTop={{ base: '140px', md: '14vh' }}
      cardBottom={{ base: '50px', lg: 'auto' }}
    >
      <Stack sx={{ width: '100%', maxWidth: 460, mx: 'auto' }}>
        <Typography variant='h4' fontWeight='700' mb='12px'>
          Join your team
        </Typography>
        <Typography variant='body2' color='text.secondary' mb='24px'>
          You've been invited to a brand workspace on TechProOne.
        </Typography>

        {loading && (
          <Stack alignItems='center' py='20px'><CircularProgress /></Stack>
        )}

        {!loading && error && (
          <Alert severity='error' sx={{ mb: '14px', borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {!loading && invite && (
          <Box
            sx={{
              p: '18px',
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'divider',
              mb: '16px',
            }}
          >
            <Typography variant='caption' color='text.secondary' display='block' mb='6px'>Brand</Typography>
            <Typography variant='h6' fontWeight='700' mb='12px'>
              {invite.brand_name || 'a brand on TechProOne'}
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='6px'>Invited email</Typography>
            <Typography mb='12px'>{invite.email}</Typography>
            <Typography variant='caption' color='text.secondary' display='block' mb='6px'>Roles</Typography>
            <Stack direction='row' gap='6px' flexWrap='wrap' mb='12px'>
              {(invite.roles || []).length === 0 && (
                <Typography variant='body2' color='text.secondary'>member</Typography>
              )}
              {(invite.roles || []).map((r) => (
                <Chip key={r} size='small' label={r} color='primary' />
              ))}
            </Stack>
            <Typography variant='caption' color='text.secondary'>
              Status: <strong>{invite.status}</strong>
              {invite.expires_at && invite.status === 'pending' && (
                <> · expires {new Date(invite.expires_at).toLocaleString()}</>
              )}
            </Typography>
          </Box>
        )}

        {!loading && invite && invite.status === 'pending' && (
          <>
            {isAuthenticated && emailMatches && (
              <Button
                variant='contained'
                disabled={accepting}
                onClick={handleAccept}
              >
                {accepting ? 'Accepting…' : 'Accept invitation'}
              </Button>
            )}

            {isAuthenticated && !emailMatches && (
              <Alert severity='warning' sx={{ borderRadius: '12px' }}>
                You're signed in as <strong>{userEmail || 'unknown'}</strong>, but this
                invite is for <strong>{invite.email}</strong>. Sign out and sign in
                with the invited email to accept.
              </Alert>
            )}

            {!isAuthenticated && (
              <Stack spacing='10px'>
                <Button variant='contained' onClick={goSignUp}>
                  Create an account
                </Button>
                <Button variant='outlined' onClick={goSignIn}>
                  I already have an account
                </Button>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </CenteredAuth>
  );
}
